// Copyright (c) 2026, Beyond Age contributors
// SPDX-License-Identifier: MIT
//
// Keeps the /login theme switch in step with Frappe's own theme preference (User.desk_theme).
//
// /login is a website page: Frappe applies no theme there and a Guest has no preference to save.
// So the desk remembers the theme it showed (remember_theme), the login page opens in it
// (read_theme), and a switch made on the login page (mark_login_choice) is written to the user's
// Frappe preference by the first desk load after login (take_pending_theme -> switch_theme).
// Storage is passed in (window.localStorage in the browser) and may be missing or blocked.
// Tests: beyond_age/tests/js/theme_sync.test.js

const THEME_KEY = "ba_theme";
const PENDING_KEY = "ba_theme_pending";
const MODES = ["light", "dark"];

function get(storage, key) {
	try {
		return storage ? storage.getItem(key) : null;
	} catch (e) {
		return null;
	}
}
function put(storage, key, value) {
	try {
		if (storage) storage.setItem(key, value);
	} catch (e) {
		// non-essential: the page still works in its default theme
	}
}
function drop(storage, key) {
	try {
		if (storage) storage.removeItem(key);
	} catch (e) {
		// non-essential
	}
}

/** "dark" | "light": the theme the login page should open in. */
export function read_theme(storage) {
	const mode = get(storage, THEME_KEY);
	return MODES.includes(mode) ? mode : "light";
}

/** Called by the desk with the mode it is showing. */
export function remember_theme(storage, mode) {
	if (MODES.includes(mode)) put(storage, THEME_KEY, mode);
}

/** Called by the login page's switch. */
export function mark_login_choice(storage, mode) {
	if (!MODES.includes(mode)) return;
	put(storage, THEME_KEY, mode);
	put(storage, PENDING_KEY, mode);
}

/**
 * On desk load: the Frappe theme ("Dark" | "Light") to save because the user switched on the
 * login page, or null. Clears the pending choice either way, so it is applied at most once.
 */
export function take_pending_theme(storage, current_desk_theme) {
	const pending = get(storage, PENDING_KEY);
	drop(storage, PENDING_KEY);
	if (!MODES.includes(pending)) return null;
	const wanted = pending === "dark" ? "Dark" : "Light";
	return wanted === current_desk_theme ? null : wanted;
}

/** Frappe's desk_theme ("Light" | "Dark" | "Automatic") -> the mode actually shown. */
export function desk_theme_to_mode(desk_theme, prefers_dark) {
	if (desk_theme === "Dark") return "dark";
	if (desk_theme === "Automatic") return prefers_dark ? "dark" : "light";
	return "light";
}
